// Importations nécessaires depuis les bibliothèques
import React, { useEffect, useState } from 'react'; // React et ses "hooks" (useEffect, useState)
import TextField from '@mui/material/TextField'; // Le champ de texte de Material-UI
import { Autocomplete, ListSubheader } from '@mui/material'; // Le composant d'autocomplétion et le sous-titre de liste
import { transform } from 'ol/proj'; // Fonction d'OpenLayers pour transformer les coordonnées (projections)

/**
 * Composant React "RechercheLocalisation"
 * Affiche une barre de recherche (Autocomplete) qui permet de trouver :
 * 1. Des adresses via une API externe (API Adresse Data.gouv)
 * 2. Des lieux locaux (cinémas) via un fichier GeoJSON
 * * Quand un lieu/adresse est sélectionné, la carte (passée en props) se centre dessus.
 */
function RechercheLocalisation({ map, center, setCenter, setCentreAdresse }) {

    // --- ÉTATS (STATES) ---

    // État pour stocker la liste des résultats (API + locaux) à afficher dans l'Autocomplete
    const [liste, setListe] = useState([]);
    
    // État pour mémoriser l'adresse ou le lieu actuellement sélectionné par l'utilisateur
    const [adresse, setAdresse] = useState({});
    
    // État pour stocker la liste des cinémas chargés depuis le fichier GeoJSON
    const [cinemaFeatures, setCinemaFeatures] = useState([]);


    /**
     * Fonction de filtrage pour l'Autocomplete.
     * REMARQUE : Dans ce code, le filtrage est déjà fait en amont dans `onInputChange`.
     * Cette fonction retourne donc simplement les options déjà filtrées.
     */
    const filterOptions = (options, { inputValue }) => {
        return options;
    }

    // --- EFFETS (USEEFFECT) ---

    // 1. Chargement des données GeoJSON des cinémas
    // Se déclenche une seule fois au montage du composant (tableau de dépendances vide `[]`)
    useEffect(() => {
        fetch(process.env.PUBLIC_URL + "/couches/cinema_audivisuel.geojson")
            .then(response => response.json()) // Convertit la réponse en JSON
            .then(data => {
                if (data && data.features) {
                    // Stocke les "features" (entités géographiques) des cinémas dans l'état
                    setCinemaFeatures(data.features); 
                }
            })
            .catch(err => console.error("Erreur de chargement du cinema_audivisuel.geojson", err));
    }, []); // `[]` signifie : ne s'exécute qu'au montage

    // 2. Centrage de la carte lors de la sélection d'une adresse
    // Se déclenche à chaque fois que l'état `adresse` est modifié
    useEffect(() => {
        // On vérifie que l'adresse est valide et a des coordonnées
        if (adresse && adresse.x && adresse.y) {
            
            let point; // La coordonnée transformée pour OpenLayers
            let zoomLevel = 15; // Niveau de zoom par défaut

            if (adresse.type === 'api') {
                // Si l'adresse vient de l'API (data.gouv), elle est en EPSG:4326 (WGS84)
                // On la transforme en EPSG:3857 (Web Mercator), le système de la carte
                point = transform([adresse.x, adresse.y], 'EPSG:4326', 'EPSG:3857');
                zoomLevel = 15;
            } 
            else if (adresse.type === 'geojson') {
                // Si l'adresse vient du GeoJSON local, on suppose qu'elle est DÉJÀ en EPSG:3857
                point = [adresse.x, adresse.y];
                zoomLevel = 17; // On zoome plus près pour un lieu spécifique
            }

            // Si on a bien un point valide
            if (point) {
                // On met à jour les états du composant parent
                setCenter(point);
                setCentreAdresse(point);
                
                // On donne l'ordre à la carte OpenLayers de se centrer et zoomer
                map.getView().setCenter(point);
                map.getView().setZoom(zoomLevel);
            }
        }
    }, [adresse]); // `[adresse]` signifie : s'exécute à chaque changement de `adresse`

    /**
     * Fonction utilitaire pour supprimer les accents d'une chaîne.
     * 'NFD' sépare la lettre de son accent (ex: 'é' -> 'e' + '´').
     * La regex remplace ensuite tous les accents (diacritiques).
     */
    function removeAccents(str) {
        if (!str) return "";
        return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    }

    // --- RENDU DU COMPOSANT ---

    return (
        <Autocomplete
            className="recherchelocalisation" // Classe CSS personnalisée
            size="small" // Taille compacte
            id="combo-box-demo" // ID pour l'accessibilité
            options={liste} // La liste des options à afficher vient de notre état `liste`
            filterOptions={filterOptions} // Utilise notre fonction de "non-filtrage"
            noOptionsText={"Aucune adresses trouvées"} // Message si `liste` est vide
            
            // Événement : quand l'utilisateur SÉLECTIONNE une option
            onChange={(event, option, reason) => {
                // On met à jour l'état `adresse` avec l'option choisie.
                // Cela déclenchera le `useEffect` de centrage de la carte.
                setAdresse(option); 
            }}
            
            // Fonction pour regrouper les résultats
            groupBy={(option) => {
                if (option.type === 'api') {
                    return 'Localisation'; // Groupe pour les adresses de l'API
                } else if (option.type === 'geojson') {
                    return 'Lieux'; // Groupe pour les cinémas locaux
                }
                return 'Autres';
            }}

            // Fonction pour personnaliser l'affichage des en-têtes de groupe
            renderGroup={(params) => (
                <li key={params.key}>
                    {/* Titre du groupe (ex: "Lieux") avec un style */}
                    <ListSubheader component="div" sx={{ 
                        bgcolor: 'rgba(0, 0, 0, 0.08)',
                        fontWeight: 600,
                        lineHeight: '32px'
                    }}>
                        {params.group}
                    </ListSubheader>
                    {/* Liste des enfants (options) de ce groupe */}
                    <ul>{params.children}</ul>
                </li>
            )}

            // Événement : quand l'utilisateur TAPE dans le champ de recherche
            onInputChange={(event, value, reason) => { 
                
                // Si l'utilisateur efface ou si la recherche est trop courte (<= 3 chars)
                if (reason === 'clear' || value.length <= 3) {
                    setListe([]); // On vide la liste des résultats
                    return; // On arrête l'exécution
                }

                // --- 1. RECHERCHE LOCALE (CINÉMAS) ---
                
                // On filtre la liste des cinémas (stockée dans `cinemaFeatures`)
                const localResults = cinemaFeatures.filter(feature => {
                    const nom = feature.properties.nom;
                    if (!nom) return false; // Sécurité

                    // Normalisation : minuscules + sans accents (des deux côtés)
                    const normalizedNom = removeAccents(nom.toLowerCase());
                    const normalizedValue = removeAccents(value.toLowerCase());

                    // On vérifie si le nom du cinéma inclut la recherche
                    return normalizedNom.includes(normalizedValue);
                });

                // On transforme les résultats locaux pour qu'ils aient le format attendu
                const mappedLocalResults = localResults.map(feature => ({
                    adressecomplete: feature.properties.nom, // Texte à afficher
                    type: 'geojson', // Type pour le `groupBy`
                    x: feature.geometry.coordinates[0], // Coordonnée X (en EPSG:3857)
                    y: feature.geometry.coordinates[1]  // Coordonnée Y (en EPSG:3857)
                }));

                // --- 2. RECHERCHE API (ADRESSES) ---

                // On transforme le centre de la carte (EPSG:3857) en Lat/Lon (EPSG:4326) pour l'API
                const [lon, lat] = transform(center, 'EPSG:3857', 'EPSG:4326');
                
                // On construit l'URL de l'API Adresse, en priorisant les résultats proches (lon/lat)
                const apiUrl = `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(value)}&limit=5&lon=${lon}&lat=${lat}`;

                // Appel à l'API
                fetch(apiUrl)
                    .then(response => response.json())
                    .then(data => {
                        let mappedApiResults = []; 
                        if (typeof data.features !== "undefined") {
                            
                            // Filtre métier : on ne garde que les résultats de la région PACA
                            const filteredFeatures = data.features.filter(adresses => 
                                adresses.properties.context && adresses.properties.context.includes('Provence-Alpes-Côte')
                            );

                            // On transforme les résultats de l'API pour qu'ils aient le format attendu
                            mappedApiResults = filteredFeatures.map(adresses => {
                                return {
                                    adressecomplete: adresses.properties.label, // Texte à afficher
                                    type: 'api', // Type pour le `groupBy`
                                    commune: adresses.properties.city,
                                    codecommune: adresses.properties.citycode,
                                    adresse: adresses.properties.name,
                                    codepostal: adresses.properties.postcode,
                                    precision: adresses.properties.type,
                                    x: adresses.geometry.coordinates[0], // Coordonnée X (en EPSG:4326)
                                    y: adresses.geometry.coordinates[1]  // Coordonnée Y (en EPSG:4326)
                                };
                            });
                        }

                        // --- FUSION DES RÉSULTATS ---
                        // On met à jour l'état `liste` en fusionnant les adresses (API) et les lieux (locaux)
                        setListe([...mappedApiResults, ...mappedLocalResults]);
                        
                    })
                    .catch(error => {
                        console.error("Erreur API Adresse:", error);
                        // On affiche au moins les résultats locaux (cinémas)
                        setListe(mappedLocalResults);
                    });
            }}
            
            // Indique à l'Autocomplete quelle propriété utiliser pour l'affichage (le texte)
            getOptionLabel={(option) => option.adressecomplete || ""} 
            
            style={{ width: 300 }}
            
            // Fonction pour "dessiner" le champ de saisie (input)
            renderInput={(params) => <TextField {...params}
                // Astuce : si une adresse est sélectionnée, on cache le label pour éviter la superposition
                label={adresse ? null : "Adresse ou lieu"}
                size="small" />}
        />
    );
}

// Exporte le composant pour qu'il puisse être importé dans d'autres fichiers
export default RechercheLocalisation;